import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Input, Label, Spinner, Table } from 'reactstrap';
import { api } from '../api/client.js';
import { useCurrentWeek } from '../hooks/useCurrentWeek.js';

export function SeasonLeaderboard() {
  const current = useCurrentWeek();
  const [seasons, setSeasons] = useState([]);
  const [season, setSeason] = useState(null);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.seasons().then(setSeasons).catch(() => setSeasons([]));
  }, []);

  useEffect(() => {
    if (current.season && season === null) setSeason(current.season);
  }, [current, season]);

  const load = useCallback(async () => {
    if (!season) return;
    setLoading(true);
    setError(null);

    try {
      setBoard(await api.seasonLeaderboard(season));
    } catch (err) {
      setError(err.message);
      setBoard(null);
    } finally {
      setLoading(false);
    }
  }, [season]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <h1 className="h3 mb-3">Season standings</h1>

      <div className="mb-3" style={{ maxWidth: 200 }}>
        <Label for="season" className="mb-1 small text-muted">Season</Label>
        <Input id="season" type="select" value={season ?? ''} disabled={seasons.length === 0}
          onChange={(e) => setSeason(Number(e.target.value))}>
          {seasons.map((s) => <option key={s.year} value={s.year}>{s.year}</option>)}
        </Input>
      </div>

      {error && <Alert fade={false} color="danger">{error}</Alert>}
      {loading && <div className="text-center py-4"><Spinner /></div>}

      {!loading && board && (
        <>
          <p className="text-muted small">
            Ranked by weeks won. {board.weeksCompleted} week{board.weeksCompleted === 1 ? '' : 's'} completed.
            Ties broken by total correct picks.
          </p>

          {board.standings.length === 0 ? (
            <Alert fade={false} color="secondary">No completed weeks yet this season.</Alert>
          ) : (
            <Table hover className="align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Player</th>
                  <th className="text-center">Weeks won</th>
                  <th className="text-center">Correct picks</th>
                  <th className="text-center">Weeks played</th>
                </tr>
              </thead>
              <tbody>
                {board.standings.map((row) => (
                  <tr key={row.userId}>
                    <td>{row.rank}</td>
                    <td>
                      {row.displayName}{' '}
                      {row.rank === 1 && row.weeksWon > 0 && <Badge color="warning" className="text-dark" pill>Leader</Badge>}
                    </td>
                    <td className="text-center fw-semibold">{row.weeksWon}</td>
                    <td className="text-center">{row.totalCorrectPicks}</td>
                    <td className="text-center text-muted">{row.weeksPlayed}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </div>
  );
}
